import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { actualizarBultos } from '../../lib/entregasApi'
import { labelVentana } from '../../lib/ventanasHorarias'

// QRCode lib se carga vía CDN (qrcodejs - mismo que el generador de QR portero)
function loadQRCodeLib() {
  return new Promise((resolve, reject) => {
    if (window.QRCode) return resolve(window.QRCode)
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
    s.onload = () => resolve(window.QRCode)
    s.onerror = reject
    document.head.appendChild(s)
  })
}

const TIPO_LABEL = {
  suscripcion: 'SUSCRIPCIÓN',
  compra_unica: 'COMPRA ÚNICA',
  productos_sueltos: 'TIENDA',
  adicional_suscripcion: 'EXTRA SUSC.',
}

const VIVIENDA_LABEL = {
  casa: 'CASA',
  edificio: 'EDIFICIO',
  oficina: 'OFICINA',
}

export default function Etiquetas() {
  const [params] = useSearchParams()
  const ids = (params.get('ids') || '').split(',').filter(Boolean)
  const [entregas, setEntregas] = useState([])
  const [loading, setLoading] = useState(true)
  const [qrReady, setQrReady] = useState(false)
  const qrRefs = useRef({})

  useEffect(() => {
    loadQRCodeLib().then(() => setQrReady(true))

    if (ids.length === 0) { setLoading(false); return }
    ;(async () => {
      const { data, error } = await supabase
        .from('entregas')
        .select(`
          id, numero_orden, tracking_token, fecha_programada, ventana_horaria,
          tipo, productos_extras, notas, bultos_total, cliente_id,
          facturacion_tipo, facturacion_razon_social, nombre_fantasia_snapshot,
          direccion_snapshot, apto_snapshot, barrio_snapshot,
          cliente:clientes!entregas_cliente_id_fkey(
            barrio, direccion, apto, dia_entrega, tipo_vivienda, alias, ocultar_contenido_etiqueta,
            titular:profiles!clientes_titular_fkey(nombre, phone),
            beneficiario:profiles!clientes_beneficiario_fkey(nombre, phone),
            portero:porteros!clientes_portero_referido_id_fkey(
              lugar,
              profile:profiles!porteros_id_fkey(nombre)
            )
          ),
          kit:kits!entregas_kit_codigo_fkey(nombre, tier)
        `)
        .in('id', ids)
      if (error) console.error(error)
      // Para entregas SIN cliente suscriptor → mergear con snapshot + pedido linkeado por numero_orden
      const entregasArr = data || []
      // Privacidad: default del toggle desde el flag persistido del suscriptor (one-shot se resuelve abajo)
      entregasArr.forEach(e => { e.incluir_contenido = !(e.cliente?.ocultar_contenido_etiqueta) })
      const sinCliente = entregasArr.filter(e => !e.cliente)
      if (sinCliente.length > 0) {
        const numeros = sinCliente.map(e => e.numero_orden).filter(Boolean)
        const { data: peds } = numeros.length ? await supabase
          .from('pedidos')
          .select(`
            numero_orden, comprador_nombre, comprador_telefono, comprador_email, items,
            entrega_direccion, entrega_apto, ocultar_contenido_etiqueta,
            barrio:barrios!pedidos_entrega_barrio_id_fkey(nombre)
          `)
          .in('numero_orden', numeros) : { data: [] }
        const byNum = Object.fromEntries((peds || []).map(p => [p.numero_orden, p]))
        entregasArr.forEach(e => {
          if (e.cliente) return
          const pe = byNum[e.numero_orden] || {}
          const esB2B = e.facturacion_tipo === 'empresa'
          const razonSocial = e.facturacion_razon_social || pe.comprador_nombre || 'Pedido único'
          const fantasia = e.nombre_fantasia_snapshot
          // Nombre visible: fantasía si existe, sino razón social
          const nombre = fantasia || razonSocial
          e.cliente = {
            alias: esB2B ? 'B2B' : null,
            barrio: e.barrio_snapshot || pe.barrio?.nombre || '',
            direccion: e.direccion_snapshot || pe.entrega_direccion || '',
            apto: e.apto_snapshot || pe.entrega_apto || '',
            tipo_vivienda: null,
            titular: { nombre, phone: pe.comprador_telefono || null },
            beneficiario: null,
            __razon_social: razonSocial,
            __fantasia: fantasia,
          }
          // Privacidad: default del toggle desde el flag persistido del pedido (compra única)
          e.incluir_contenido = !(pe.ocultar_contenido_etiqueta)
          // Si productos_extras está vacío pero el pedido tiene items, usar los items del pedido
          if ((!e.productos_extras || e.productos_extras.length === 0) && pe.items?.length) {
            e.productos_extras = pe.items
          }
        })
      }
      setEntregas(entregasArr)
      setLoading(false)
    })()
  }, [params])

  // Renderiza QRs cuando todo está listo (uno por bulto)
  useEffect(() => {
    if (!qrReady || entregas.length === 0) return
    entregas.forEach(e => {
      const total = e.bultos_total || 1
      for (let i = 1; i <= total; i++) {
        const key = `${e.id}-${i}`
        const qrDiv = qrRefs.current[key]
        if (qrDiv && qrDiv.children.length === 0) {
          new window.QRCode(qrDiv, {
            text: `https://limpack.uy/d/${e.tracking_token}`,
            width: 160, height: 160,
            colorDark: '#000000', colorLight: '#FFFFFF',
            correctLevel: window.QRCode.CorrectLevel.M,
          })
        }
      }
    })
  }, [qrReady, entregas])

  const ventanaLabel = (v) => labelVentana(v).toUpperCase()
  const fechaLabel = (f) => {
    if (!f) return ''
    const d = new Date(f + 'T12:00:00')
    return d.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' }).toUpperCase()
  }

  async function cambiarBultos(entregaId, nuevo) {
    const n = Math.max(1, Math.min(20, nuevo))
    // optimistic UI
    setEntregas(es => es.map(e => e.id === entregaId ? { ...e, bultos_total: n } : e))
    // limpia los refs viejos para que se vuelvan a renderizar
    Object.keys(qrRefs.current).forEach(k => { if (k.startsWith(entregaId + '-')) delete qrRefs.current[k] })
    try { await actualizarBultos(entregaId, n) } catch (err) { console.error('actualizarBultos:', err) }
  }

  // Privacidad: incluir o no el bloque CONTENIDO/DETALLE. Persiste la preferencia en el cliente (suscriptor) o en el pedido (compra única).
  async function toggleContenido(entregaId) {
    let target = null
    setEntregas(es => es.map(e => {
      if (e.id !== entregaId) return e
      const nuevo = !(e.incluir_contenido !== false)
      target = { cliente_id: e.cliente_id, numero_orden: e.numero_orden, ocultar: !nuevo }
      return { ...e, incluir_contenido: nuevo }
    }))
    if (!target) return
    try {
      if (target.cliente_id) {
        await supabase.from('clientes').update({ ocultar_contenido_etiqueta: target.ocultar }).eq('id', target.cliente_id)
      } else if (target.numero_orden) {
        await supabase.from('pedidos').update({ ocultar_contenido_etiqueta: target.ocultar }).eq('numero_orden', target.numero_orden)
      }
    } catch (err) { console.error('persist ocultar_contenido_etiqueta:', err) }
  }

  if (loading) return <div className="loading">Cargando etiquetas...</div>

  if (entregas.length === 0) {
    return (
      <div className="page" style={{ padding: 32 }}>
        <h1>Sin etiquetas</h1>
        <p>No se encontraron entregas para imprimir. Volvé a /admin/entregas y seleccioná entregas.</p>
      </div>
    )
  }

  // Total de etiquetas = suma de bultos
  const totalLabels = entregas.reduce((s, e) => s + (e.bultos_total || 1), 0)

  return (
    <>
      <div className="etiquetas-toolbar no-print">
        <div>
          <strong>{totalLabels}</strong> etiquetas · {entregas.length} entrega{entregas.length !== 1 ? 's' : ''} · 100×150mm
        </div>
        <div>
          <button className="btn primary" onClick={() => window.print()}>🖨️ Imprimir</button>
          <button className="btn ghost" onClick={() => window.history.back()} style={{ marginLeft: 8 }}>← Volver</button>
        </div>
      </div>

      {/* Panel para configurar bultos por entrega — solo en pantalla */}
      <div className="bultos-config no-print">
        <div className="bc-title">Bultos por entrega</div>
        <div className="bc-list">
          {entregas.map(e => (
            <div key={e.id} className="bc-row">
              <div className="bc-info">
                <div className="bc-orden">{e.numero_orden}</div>
                <div className="bc-nombre">{(e.cliente?.beneficiario || e.cliente?.titular)?.nombre || '—'} · {e.cliente?.barrio}</div>
              </div>
              <div className="bc-controls">
                <button onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) - 1)} disabled={(e.bultos_total || 1) <= 1}>−</button>
                <span className="bc-num">{e.bultos_total || 1}</span>
                <button onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) + 1)}>+</button>
                <span className="bc-bulto">bulto{(e.bultos_total || 1) !== 1 ? 's' : ''}</span>
              </div>
              <label className="bc-contenido" title="Se guarda para este cliente/pedido. Destildado: la etiqueta se imprime sin el detalle de productos (privacidad).">
                <input type="checkbox" checked={e.incluir_contenido !== false} onChange={() => toggleContenido(e.id)} />
                Incluir contenido
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="et-print">
      {entregas.map(e => {
        const total = e.bultos_total || 1
        const labels = Array.from({ length: total }, (_, i) => i + 1).map(idx => {
          const key = `${e.id}-${idx}`
          const tipoLabel = TIPO_LABEL[e.tipo] || e.tipo?.toUpperCase() || ''
          return (
            <div key={key} className={e.incluir_contenido === false ? 'etiqueta sin-contenido' : 'etiqueta'}>
              {/* HEADER NEGRO: Limpack + tagline / bulto círculo */}
              <div className="et-head">
                <div className="et-brand-block">
                  <div className="et-logo-text">Lim<strong>pack</strong></div>
                  <div className="et-tagline">Limpieza, resuelta cada mes</div>
                </div>
                <div className="et-bulto">
                  <div className="et-bulto-num">{idx}<span className="et-bulto-de">/</span>{total}</div>
                  <div className="et-bulto-label">BULTO</div>
                </div>
              </div>

              {/* BANDA NEGRA: tipo + fecha + ventana */}
              <div className="et-fecha-band">
                <span>{tipoLabel}</span>
                <span className="et-fecha-sep">·</span>
                <span>{fechaLabel(e.fecha_programada)}</span>
                <span className="et-fecha-sep">·</span>
                <span>{ventanaLabel(e.ventana_horaria)}</span>
              </div>

              {/* CAJA ORDEN + PAQUETE en 2 columnas */}
              <div className="et-paq-row">
                <div className="et-paq-col et-paq-col-orden-box">
                  <div className="et-paq-col-lab">ORDEN</div>
                  <div className="et-paq-col-orden">{e.numero_orden || '—'}</div>
                </div>
                <div className="et-paq-col et-paq-col-num-box">
                  <div className="et-paq-col-lab">PAQUETE</div>
                  <div className="et-paq-col-num">#{(e.numero_orden || '').split('-').pop() || '?'}</div>
                </div>
              </div>

              {/* DESTINATARIO */}
              <div>
                <span className="et-dest-label">DESTINATARIO</span>
                <div className="et-nombre">
                  {e.cliente?.__fantasia || (e.cliente?.beneficiario || e.cliente?.titular)?.nombre || '—'}
                </div>
                {e.cliente?.__fantasia && e.cliente?.__razon_social && (
                  <div className="et-razon">{e.cliente.__razon_social}</div>
                )}
              </div>

              {/* TELÉFONO — caja con borde grueso (para llamar si no atiende) */}
              {(() => {
                const tel = (e.cliente?.beneficiario || e.cliente?.titular)?.phone
                if (!tel) return null
                return (
                  <div className="et-tel-row">
                    <span className="et-tel-ico">☎</span>
                    <span className="et-tel-num">{tel}</span>
                    <span className="et-tel-lab">SI NO<br/>ATIENDE</span>
                  </div>
                )
              })()}

              {/* DIRECCIÓN — caja con borde grueso */}
              <div className="et-direccion">
                <div className="et-barrio-row">
                  <span className="et-barrio">{e.cliente?.barrio}</span>
                  {e.cliente?.tipo_vivienda && (
                    <span className="et-vivienda">{VIVIENDA_LABEL[e.cliente.tipo_vivienda]}</span>
                  )}
                </div>
                <div className="et-calle">{e.cliente?.direccion}{e.cliente?.apto ? ` · ${e.cliente.apto}` : ''}</div>
              </div>

              {/* PORTERO REFERIDO (si vino por portero) */}
              {e.cliente?.portero && (
                <div className="et-referido">
                  <span className="et-referido-tag">PORTERO REFERIDO</span>
                  <span className="et-referido-txt">
                    {e.cliente.portero.profile?.nombre}
                    {e.cliente.portero.lugar ? ` · ${e.cliente.portero.lugar}` : ''}
                  </span>
                </div>
              )}

              {/* NOTAS */}
              {e.notas && (
                <div className="et-notas">
                  <div className="et-notas-label">★ NOTAS DEL CLIENTE</div>
                  <div className="et-notas-txt">{e.notas}</div>
                </div>
              )}

              {/* QR + CONTENIDO en 2 columnas */}
              <div className="et-bottom">
                <div className="et-qr-block">
                  <img
                    className="et-qr-img"
                    src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(`https://limpack.uy/d/${e.tracking_token}`)}&size=200x200&margin=0`}
                    alt="QR entrega"
                  />
                  <div className="et-qr-cap">QR ENTREGA</div>
                </div>

                <div className="et-kit">
                  <div className="et-kit-label">CONTENIDO</div>
                  <div className="et-kit-nombre">
                    {(() => {
                      if (e.kit?.nombre) return e.kit.nombre
                      const extras = e.productos_extras || []
                      if (extras.length === 0) return '— sin contenido —'
                      // OJO: hay que contar UNIDADES, no líneas. Antes mostraba extras.length y en
                      // un pedido con "2 × Jabón" decía 11 cuando en la caja van 13 → quien arma
                      // cuenta 11 y el cliente recibe incompleto. (Caso real P-000051, 27-jul.)
                      const unidades = extras.reduce((s, x) => s + (Number(x?.cantidad ?? x?.qty ?? 1) || 1), 0)
                      return unidades === extras.length
                        ? `${unidades} productos sueltos`
                        : `${unidades} unidades · ${extras.length} productos`
                    })()}
                  </div>
                  {(e.productos_extras || []).length > 0 && (
                    <div className="et-extras">
                      <div className="et-extras-label">{e.kit?.nombre ? '+ EXTRAS' : 'DETALLE'}</div>
                      <ul className="et-extras-list">
                        {(e.productos_extras || []).map((x, i) => {
                          const cantidad = x?.cantidad ?? x?.qty ?? 1
                          const nombre = typeof x === 'string'
                            ? x
                            : (x?.descripcion || x?.nombre || x?.sku || 'Producto')
                          return <li key={i}>{cantidad} × {nombre}</li>
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer — agradecimiento + web */}
              <div className="et-foot">
                <span className="et-gracias">¡Gracias por tu compra!</span>
                <span className="et-web">limpack.uy</span>
              </div>
            </div>
          )
        })
        // One-shot (compra única / tienda): sumar una etiqueta de agradecimiento por pedido
        if (['compra_unica', 'productos_sueltos'].includes(e.tipo)) {
          labels.push(<EtiquetaGracias key={`${e.id}-gracias`} e={e} />)
        }
        return labels
      })}
      </div>

      <style>{`
        @page { size: 100mm 150mm; margin: 0; }
        @media print {
          body { background: #fff; margin: 0; }
          .no-print { display: none !important; }
          /* Salto ANTES de cada etiqueta menos la primera → nunca queda una hoja
             fantasma al final. (El page-break-after: always dejaba un salto colgando
             que la térmica sacaba como un papel en blanco de más.) */
          .etiqueta { box-shadow: none !important; margin: 0 !important; page-break-after: auto; break-after: auto; }
          .et-print > .etiqueta + .etiqueta {
            page-break-before: always !important;
            break-before: page !important;
          }
          /* La ultima etiqueta nunca fuerza salto despues -> cero hoja fantasma al final. */
          .et-print > .etiqueta:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          /* Sin márgenes residuales que empujen contenido a una página extra */
          html, body { height: auto !important; margin: 0 !important; padding: 0 !important; }

          /* ── IMPRESORA TÉRMICA: TODO BLANCO, TEXTO NEGRO ──────────────────────
             Decisión de Nico (27-jul) y es lo correcto para térmica:
               · No imprime grises → los simula con puntos dispersos y sale lavado.
               · Las áreas negras grandes salen manchadas (mucha energía del cabezal)
                 y además lo desgastan más rápido.
             Entonces: cero fondos negros. Todo papel blanco con texto negro pleno y
             bordes gruesos para dar estructura. Máxima nitidez y legibilidad.
             (En pantalla la etiqueta sigue viéndose con sus franjas negras.) */
          .etiqueta, .etiqueta * {
            background: transparent !important;
            color: #000 !important;
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            text-shadow: none !important;
            filter: none !important;
          }
          .etiqueta { background: #fff !important; }
          /* Lo que era franja negra ahora se marca con borde grueso */
          .etiqueta .et-head,
          .etiqueta .et-fecha-band {
            border: 0.6mm solid #000 !important;
          }
          /* Los sellos chicos quedan como etiqueta con recuadro */
          .etiqueta .et-dest-label,
          .etiqueta .et-tel-ico,
          .etiqueta .et-vivienda,
          .etiqueta .et-referido-tag {
            border: 0.35mm solid #000 !important;
          }
          /* Pesos: nada fino, que se imprime entrecortado */
          .etiqueta * { font-weight: 700 !important; }
          .etiqueta .et-nombre,
          .etiqueta .et-tel-num,
          .etiqueta .et-direccion,
          .etiqueta .et-barrio,
          .etiqueta .et-kit-nombre { font-weight: 900 !important; }
          .etiqueta [class*="et-"] { border-color: #000 !important; }
          /* QR: siempre negro sobre blanco y sin suavizado */
          .etiqueta .et-qr img, .etiqueta .et-qr canvas, .etiqueta .et-qr svg {
            image-rendering: pixelated !important;
            background: #fff !important;
          }
        }
        @media screen {
          body { background: #E5E7EB; }
        }

        .etiquetas-toolbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          background: var(--bg-dark); color: #fff;
          padding: 12px 24px; display: flex; align-items: center; justify-content: space-between;
        }

        .bultos-config {
          max-width: 100mm; margin: 76px auto 12mm; background: #fff;
          border-radius: 10px; padding: 16px; box-shadow: 0 4px 14px rgba(0,0,0,.08);
        }
        .bc-title {
          font-size: 11px; font-weight: 800; letter-spacing: .12em;
          text-transform: uppercase; color: #555; margin-bottom: 10px;
          padding-bottom: 8px; border-bottom: 1px solid #eee;
        }
        .bc-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 0; border-bottom: 1px dashed #eee;
        }
        .bc-row:last-child { border-bottom: none }
        .bc-info { flex: 1; min-width: 0 }
        .bc-orden { font-family: monospace; font-size: 11px; color: #888 }
        .bc-nombre { font-size: 13px; font-weight: 600; color: #111 }
        .bc-controls { display: flex; align-items: center; gap: 6px }
        .bc-controls button {
          width: 28px; height: 28px; border-radius: 6px;
          background: #111; color: #fff; border: 0; font-size: 16px;
          cursor: pointer; line-height: 1;
        }
        .bc-controls button:disabled { background: #ccc; cursor: not-allowed }
        .bc-num {
          min-width: 22px; text-align: center; font-weight: 800; font-size: 16px;
        }
        .bc-bulto { font-size: 11px; color: #666; margin-left: 4px }
        .bc-contenido {
          display: flex; align-items: center; gap: 6px; margin-left: 12px;
          font-size: 12px; font-weight: 600; color: #334155; cursor: pointer; white-space: nowrap;
        }
        .bc-contenido input { width: 15px; height: 15px; cursor: pointer; margin: 0 }

        /* ============ ETIQUETA v6 (B&N para impresora térmica) ============ */
        .etiqueta {
          width: 100mm; height: 150mm;
          background: #fff; color: #000;
          margin: 0 auto 12mm;
          padding: 2.5mm;
          font-family: 'Helvetica', 'Inter', Arial, sans-serif;
          box-shadow: 0 4px 16px rgba(0,0,0,.15);
          display: flex; flex-direction: column; gap: 1.5mm;
          page-break-inside: avoid;
          break-inside: avoid;
          overflow: hidden;
          border: 1px solid #000;
          box-sizing: border-box;
        }
        /* El salto entre etiquetas lo maneja el break-before en @media print
           (.et-print > .etiqueta + .etiqueta) — así nunca queda una hoja en blanco al final. */

        /* HEADER NEGRO con Limpack + tagline + bulto círculo */
        .et-head {
          background: #000; color: #fff;
          padding: 2mm 2.5mm; margin: -2.5mm -2.5mm 0;
          display: flex; justify-content: space-between; align-items: center;
        }
        .et-brand-block { display: flex; flex-direction: column; }
        .et-logo-text {
          font-size: 19pt; font-weight: 400; letter-spacing: -0.5pt; line-height: 1;
        }
        .et-logo-text strong { font-weight: 900; }
        .et-tagline {
          font-size: 9pt; font-weight: 800; letter-spacing: 0.2pt; margin-top: 1mm;
        }
        .et-bulto {
          background: #fff; color: #000;
          width: 12mm; height: 12mm; border-radius: 50%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
        }
        .et-bulto-num { font-size: 13pt; font-weight: 900; line-height: 1; }
        .et-bulto-de { font-weight: 400; opacity: 0.6; margin: 0 1px; font-size: 11pt; }
        .et-bulto-label { font-size: 5pt; font-weight: 900; letter-spacing: 0.6pt; margin-top: 0.5mm; }

        /* BANDA NEGRA — tipo + fecha + ventana */
        .et-fecha-band {
          background: #000; color: #fff;
          padding: 1.6mm 2mm;
          font-size: 9pt; font-weight: 900; letter-spacing: 0.3pt;
          text-align: center;
          margin: 0 -2.5mm;
        }
        .et-fecha-sep { margin: 0 1.5mm; opacity: 0.55 }

        /* CAJA ORDEN + PAQUETE en 2 columnas */
        .et-paq-row { display: flex; border: 0.7mm solid #000; align-items: stretch; }
        .et-paq-col { padding: 1.5mm; text-align: center; display: flex; flex-direction: column; justify-content: center; }
        .et-paq-col-orden-box { flex: 1; }
        .et-paq-col-num-box { flex: 1.7; }   /* PAQUETE domina: el repartidor lo cruza con la app */
        .et-paq-col + .et-paq-col { border-left: 0.7mm solid #000; }
        .et-paq-col-lab { font-size: 7pt; font-weight: 900; letter-spacing: 1pt; }
        .et-paq-col-orden { font-size: 10pt; font-weight: 900; letter-spacing: -0.2pt; line-height: 1; margin-top: 0.8mm; }
        .et-paq-col-num { font-size: 36pt; font-weight: 900; letter-spacing: -1pt; line-height: 0.95; margin-top: 0.3mm; }

        /* DESTINATARIO */
        .et-dest-label {
          background: #000; color: #fff; padding: 0.8mm 1.8mm;
          font-size: 7pt; font-weight: 900; letter-spacing: 0.7pt;
          display: inline-block;
        }
        .et-nombre {
          font-size: 12pt; font-weight: 900; line-height: 1.05;
          margin-top: 0.8mm;
        }
        .et-razon {
          font-size: 7.5pt; font-weight: 600; line-height: 1.1;
          color: #333; margin-top: 0.5mm; font-style: italic;
        }

        /* TELÉFONO — caja con borde grueso */
        .et-tel-row {
          border: 0.6mm solid #000; padding: 1.2mm 1.5mm;
          display: flex; align-items: center; gap: 2mm;
        }
        .et-tel-ico {
          background: #000; color: #fff;
          width: 6mm; height: 6mm;
          display: flex; align-items: center; justify-content: center;
          font-size: 11pt; font-weight: 900;
        }
        .et-tel-num { font-size: 11pt; font-weight: 900; flex: 1; letter-spacing: 0.2pt; }
        .et-tel-lab { font-size: 6.5pt; font-weight: 900; line-height: 1.05; text-align: right; }

        /* DIRECCIÓN */
        .et-direccion {
          border: 0.6mm solid #000;
          padding: 1.2mm 1.5mm;
        }
        .et-barrio-row {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 0.5mm; gap: 2mm;
        }
        .et-barrio {
          font-size: 9.5pt; font-weight: 900; letter-spacing: 0.3pt;
        }
        .et-vivienda {
          background: #000; color: #fff;
          padding: 0.6mm 2mm;
          font-size: 7pt; font-weight: 900; letter-spacing: 0.3pt;
          white-space: nowrap;
        }
        .et-calle { font-size: 10.5pt; font-weight: 900; line-height: 1.2 }

        /* PORTERO REFERIDO */
        .et-referido {
          display: flex; align-items: center; gap: 1.5mm;
          font-size: 8pt; line-height: 1.2;
        }
        .et-referido-tag {
          background: #000; color: #fff;
          padding: 0.6mm 1.8mm;
          font-weight: 900; letter-spacing: 0.4pt; font-size: 7pt;
          flex-shrink: 0;
        }
        .et-referido-txt {
          font-weight: 700;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }

        /* NOTAS */
        .et-notas {
          border: 0.5mm solid #000;
          padding: 1mm 1.5mm;
          font-size: 8pt; line-height: 1.25;
        }
        .et-notas-label {
          font-weight: 900; font-size: 7pt; letter-spacing: 0.4pt;
          margin-bottom: 0.5mm;
        }
        .et-notas-txt { font-weight: 600; }

        /* BOTTOM — QR + CONTENIDO */
        .et-bottom {
          display: flex; gap: 2.5mm;
          flex: 1; min-height: 0;
        }
        .et-qr-block {
          text-align: center;
          flex-shrink: 0;
        }
        .et-qr-img {
          width: 22mm; height: 22mm;
          display: block;
          image-rendering: pixelated;
        }
        .et-qr-cap { font-size: 7pt; font-weight: 900; letter-spacing: 0.6pt; margin-top: 0.6mm; text-align: center; }

        .et-kit { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }
        .etiqueta.sin-contenido .et-kit { display: none; }
        .etiqueta.sin-contenido .et-bottom { justify-content: center; align-items: center; }
        .et-kit-label { font-size: 7pt; font-weight: 900; letter-spacing: 0.8pt; }
        .et-kit-nombre { font-size: 10.5pt; font-weight: 900; line-height: 1.05; margin-top: 0.5mm; }
        .et-extras { margin-top: 0.8mm; overflow: hidden; }
        .et-extras-label { font-size: 6.5pt; font-weight: 900; letter-spacing: 0.6pt; }
        .et-extras-list {
          margin: 0.3mm 0 0 0; padding-left: 0;
          font-size: 6.5pt; line-height: 1.15;
        }
        .et-extras-list li {
          list-style: none; padding-left: 1.5mm; text-indent: -1.5mm;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .et-extras-mas { font-size: 6pt; font-weight: 700; font-style: italic; margin-top: 0.3mm; }

        /* FOOTER — agradecimiento + web */
        .et-foot {
          text-align: center; padding-top: 1mm;
          border-top: 0.5mm solid #000;
          display: flex; flex-direction: column; gap: 0.3mm;
        }
        .et-gracias { font-size: 8.5pt; font-weight: 700; letter-spacing: 0.2pt; }
        .et-web { font-size: 10pt; font-weight: 800; letter-spacing: 0.3pt; }

        /* ============ ETIQUETA DE AGRADECIMIENTO (one-shot) ============ */
        .etiqueta-gracias { justify-content: flex-start; }
        .eg-body {
          flex: 1; display: flex; flex-direction: column;
          align-items: center; justify-content: center; text-align: center;
          gap: 4mm; padding: 4mm 2mm;
        }
        .eg-gracias {
          font-size: 30pt; font-weight: 900; line-height: 0.98; letter-spacing: -0.5pt;
        }
        .eg-msg {
          font-size: 10.5pt; font-weight: 700; line-height: 1.3; max-width: 82mm;
        }
        .eg-seguinos {
          display: flex; align-items: center; gap: 3mm;
          border: 0.7mm solid #000; padding: 2.5mm 3mm; margin-top: 1mm;
        }
        .eg-qr { width: 26mm; height: 26mm; display: block; image-rendering: pixelated; flex-shrink: 0; }
        .eg-seguinos-txt { text-align: left; }
        .eg-seg-lab { font-size: 7.5pt; font-weight: 900; letter-spacing: 0.6pt; }
        .eg-handle { font-size: 16pt; font-weight: 900; line-height: 1; margin: 0.8mm 0; }
        .eg-seg-sub { font-size: 7.5pt; font-weight: 600; line-height: 1.15; }
        .eg-foot {
          padding-top: 1.5mm; border-top: 0.5mm solid #000;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 9pt; font-weight: 800;
        }
        .eg-foot-web { font-size: 10.5pt; font-weight: 900; }

        body { padding-top: 56px; }
      `}</style>
    </>
  )
}

/* Etiqueta de agradecimiento para pedidos one-shot (una por pedido).
   Térmica B&N: hereda .etiqueta (page-break + override de impresión). QR a Instagram. */
function EtiquetaGracias({ e }) {
  const nombre = e.cliente?.__fantasia || (e.cliente?.beneficiario || e.cliente?.titular)?.nombre || null
  const primerNombre = nombre ? String(nombre).trim().split(/\s+/)[0] : null
  const igUrl = 'https://instagram.com/limpackuy'
  return (
    <div className="etiqueta etiqueta-gracias">
      <div className="et-head">
        <div className="et-brand-block">
          <div className="et-logo-text">Lim<strong>pack</strong></div>
          <div className="et-tagline">Limpieza, resuelta cada mes</div>
        </div>
      </div>

      <div className="eg-body">
        <div className="eg-gracias">¡Gracias por<br />tu compra!</div>
        <div className="eg-msg">
          {primerNombre ? `${primerNombre}, gracias ` : 'Gracias '}por elegir Limpack.
          Ojalá te resuelva la limpieza. Cualquier cosa, escribinos y lo vemos.
        </div>

        <div className="eg-seguinos">
          <img
            className="eg-qr"
            src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(igUrl)}&size=220x220&margin=0`}
            alt="QR Instagram Limpack"
          />
          <div className="eg-seguinos-txt">
            <div className="eg-seg-lab">SEGUINOS EN INSTAGRAM</div>
            <div className="eg-handle">@limpackuy</div>
            <div className="eg-seg-sub">Novedades, tips y sorteos</div>
          </div>
        </div>
      </div>

      <div className="eg-foot">
        <span>WhatsApp 098 610 001</span>
        <span className="eg-foot-web">limpack.uy</span>
      </div>
    </div>
  )
}
